import { useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { getStatsMembers } from '../../lib/api';
import { pctDelta } from '../../lib/format';
import { KpiCard } from '../../components/stats/KpiCard';
import { BarChart } from '../../components/stats/BarChart';
import { BreakdownList } from '../../components/stats/BreakdownList';
import { SectionFrame } from '../../components/stats/SectionFrame';
import { PS, T } from '../../lib/theme';
import type { MemberStats } from '../../lib/types';

export function MembersSection({ start, end }: { start: string; end: string }) {
  const loader = useCallback(
    (signal: AbortSignal) => getStatsMembers(start, end, signal),
    [start, end],
  );
  const { data, loading, error } = useApi<MemberStats>(loader);

  return (
    <SectionFrame title="Members & subscriptions" loading={loading} error={error}>
      {data && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <KpiCard
              label="New signups"
              value={data.new_signups.toLocaleString('en-US')}
              delta={pctDelta(data.new_signups, data.prev_new_signups)}
            />
            <KpiCard label="Active members" value={data.active_members.toLocaleString('en-US')} />
            <KpiCard label="Expiring in 7d" value={data.expiring_soon.toLocaleString('en-US')} />
          </div>
          <div style={{ ...T.caption, marginTop: -4 }}>
            &quot;Expiring in 7d&quot; is a fixed forward 7-day horizon and does not change with the selected period.
          </div>
          <div>
            <div style={{ ...T.label, marginBottom: 6 }}>New signups per day</div>
            <BarChart
              data={data.daily.map((d) => ({ label: d.date, value: d.value }))}
              color={PS.success}
              format={(value) => `${value.toLocaleString('en-US')} ${value === 1 ? 'signup' : 'signups'}`}
            />
          </div>
          <div>
            <div style={{ ...T.label, marginBottom: 6 }}>Active subscriptions by plan (current)</div>
            <BreakdownList data={data.active_subscriptions_by_plan} />
          </div>
        </>
      )}
    </SectionFrame>
  );
}
